import { config } from '@euler/config';
import { useEffect, useMemo, useRef } from 'react';
import Toast from 'react-native-root-toast';
import { SpeechStart } from '../capture/capturePage';
import { SpeechCaptureHandle } from '../capture/speechCaptureTypes';
import { dictationEndpoint } from '../capture/speechEndpoint';
import { VoiceTurnVia, useVoiceTurn } from './useVoiceTurn';
import { kVoiceNoticeText } from './voiceNoticeText';

/**
 * A dictation's start: the signed-in user is who speaks. A tap to dictate,
 * which nothing else ends, ends once the speaker goes quiet; a hold ends as
 * it is let go.
 */
const dictationStartOf = (via: VoiceTurnVia): SpeechStart =>
  via === 'dictate' ? { endOnSilence: true } : {};
const kNothing = () => undefined;

/**
 * A voice turn on chat-gateway's dictation: the words recognised as they
 * are spoken, nothing of the voice kept. Why words did not get through
 * shows as a toast. The host mounts the capture: a `SpeechCapture` on
 * `endpoint` (null: no voice, typing still works) with `capture`.
 */
export function useDictationTurn(
  /** Words held and let go of on 发送. */
  onLine: (text: string) => void,
) {
  const capture = useRef<SpeechCaptureHandle>(null);
  const endpoint = useMemo(() => {
    try {
      return dictationEndpoint(config.chatEndpoint, __DEV__);
    } catch (error) {
      console.error('dictation endpoint unavailable', error);
      return null;
    }
  }, []);
  const line = useRef(onLine);
  line.current = onLine;
  const voice = useVoiceTurn(
    capture,
    dictationStartOf,
    text => line.current(text),
    kNothing,
  );
  useEffect(() => {
    const notices = voice.notice$.subscribe(notice => {
      if (notice)
        Toast.show(kVoiceNoticeText[notice], {
          duration: Toast.durations.SHORT,
          position: Toast.positions.CENTER,
        });
    });
    return () => notices.unsubscribe();
  }, [voice]);
  return { voice, capture, endpoint };
}
